// Parent Progress tab: Code Lab summary — real completed coding exercises,
// the Computing skills earned through them, and in-app badges per child.
import { useEffect, useState } from "react";
import { Award, Printer, Terminal } from "lucide-react";
import { API, api } from "../../lib/api";

const LEVEL = { learning: "Learning", practicing: "Practicing",
  proficient: "Proficient", mastered: "Mastered" };

// Band-completion badges with a parent-controlled printable certificate
// (download stays inside the guardian's account — no public sharing).
const CERT_BADGES = new Set(["clb_explorer_coder", "clb_builder_coder",
  "clb_inventor_coder"]);

const printCert = (childId, badgeId) => window.open(
  `${API}/codelab/family/certificate.png?child_id=${childId}&badge_id=${badgeId}`,
  "_blank");

export const CodeLabSummaryCard = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/codelab/family/summary")
      .then(({ data }) => setRows(data.children || []))
      .catch(() => setRows([]));
  }, []);
  if (!rows.length) return null;
  return (
    <section className="bg-card rounded-2xl border border-border p-5"
      data-testid="codelab-summary-card">
      <h2 className="text-base md:text-lg font-bold font-display flex items-center gap-2 mb-3">
        <Terminal className="h-5 w-5 text-violet-600" aria-hidden="true" />
        Code Lab — real Python coding
      </h2>
      <div className="space-y-2">
        {rows.map((c) => (
          <button key={c.child_id} type="button"
            onClick={() => setOpen(open === c.child_id ? null : c.child_id)}
            className="w-full text-left rounded-xl border border-border p-3 hover:border-violet-300"
            data-testid={`codelab-summary-${c.child_id}`}>
            <div className="flex justify-between gap-2 text-sm">
              <span className="font-bold">{c.child_name}</span>
              <span className="text-xs text-muted-foreground shrink-0">
                {c.completed}/{c.available} exercises completed
              </span>
            </div>
            {c.badges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {c.badges.map((b) => (
                  <span key={b.badge_id} title={b.desc}
                    className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 text-amber-900 px-2 py-0.5 text-xs font-bold"
                    data-testid={`codelab-summary-badge-${b.badge_id}`}>
                    <Award className="h-3 w-3" aria-hidden="true" />{b.name}
                    {CERT_BADGES.has(b.badge_id) && (
                      <span role="button" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); printCert(c.child_id, b.badge_id); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); printCert(c.child_id, b.badge_id); } }}
                        className="inline-flex items-center gap-0.5 rounded-full bg-white border border-amber-400 px-1.5 py-0.5 text-[10px] font-extrabold text-amber-800 hover:bg-amber-100 cursor-pointer"
                        data-testid={`codelab-cert-print-${c.child_id}-${b.badge_id}`}>
                        <Printer className="h-2.5 w-2.5" aria-hidden="true" />Certificate
                      </span>
                    )}
                  </span>
                ))}
              </div>
            )}
            {open === c.child_id && (
              <div className="mt-2 text-xs space-y-1"
                data-testid={`codelab-summary-detail-${c.child_id}`}>
                {c.exercises.length > 0 && (
                  <p><span className="font-bold">Completed:</span>{" "}
                    {c.exercises.map((e) =>
                      `${e.title}${e.independent ? " (solo)" : ""}`).join(", ")}
                  </p>
                )}
                {c.skills.length > 0 && (
                  <p><span className="font-bold">Skills earned:</span>{" "}
                    {c.skills.map((s) =>
                      `${s.title} — ${LEVEL[s.level] || s.level}`).join(" · ")}
                  </p>
                )}
                {c.in_progress > 0 && (
                  <p className="text-muted-foreground">
                    {c.in_progress} exercise{c.in_progress > 1 ? "s" : ""} in progress
                  </p>
                )}
              </div>
            )}
          </button>
        ))}
      </div>
    </section>
  );
};
